/**
 * ConfigService - Core configuration management for the app
 * 
 * This service loads and manages all JSON configurations.
 * It's language-agnostic and version-agnostic - behavior is determined by config files.
 */

// These values can be set via environment variables for development
// In production builds, these are set during the build process
const ACTIVE_LANGUAGE = 'javascript'; // Set during build for: javascript
const VERSION_TYPE = 'free'; // Set during build for: free

class ConfigService {
  constructor() {
    this.config = null;
    this.lessons = null;
    this.interviews = null;
    this.challenges = null;
    this.achievements = null;
    this.playground = null;
    this.mascot = null;
    this.isLoaded = false;
    this.loadPromise = null;
  }

  /**
   * Get the active language
   */
  getLanguage() {
    return ACTIVE_LANGUAGE;
  }

  /**
   * Get the version type (free/paid)
   */
  getVersionType() {
    return VERSION_TYPE;
  }

  /**
   * Check if this is a free version
   */
  isFreeVersion() {
    return VERSION_TYPE === 'free';
  }

  /**
   * Check if this is a paid version
   */
  isPaidVersion() {
    return VERSION_TYPE === 'paid';
  }

  /**
   * Load all configuration files
   * Returns a promise that resolves when all configs are loaded
   */
  async loadAll() {
    // Prevent multiple simultaneous loads
    if (this.loadPromise) {
      return this.loadPromise;
    }

    this.loadPromise = this._loadConfigs();
    return this.loadPromise;
  }

  async _loadConfigs() {
    try {
      const basePath = `/assets/configs/${ACTIVE_LANGUAGE}`;
      
      // Load version-specific app config
      const configFile = `app.config.${VERSION_TYPE}.json`;
      const configRes = await fetch(`${basePath}/${configFile}`);
      if (!configRes.ok) {
        throw new Error(`Failed to load ${configFile}: ${configRes.status}`);
      }
      this.config = await configRes.json();

      // Load shared content files in parallel
      const [
        lessonsRes,
        interviewsRes,
        challengesRes,
        achievementsRes,
        playgroundRes,
        mascotRes
      ] = await Promise.all([
        fetch(`${basePath}/lessons.json`),
        fetch(`${basePath}/interviews.json`),
        fetch(`${basePath}/challenges.json`),
        fetch(`${basePath}/achievements.json`),
        fetch(`${basePath}/playground.json`),
        fetch(`${basePath}/mascot.json`)
      ]);

      // Parse all responses
      this.lessons = lessonsRes.ok ? await lessonsRes.json() : { lessons: [], categories: [] };
      this.interviews = interviewsRes.ok ? await interviewsRes.json() : { questions: [] };
      this.challenges = challengesRes.ok ? await challengesRes.json() : { challenges: [] };
      this.achievements = achievementsRes.ok ? await achievementsRes.json() : { badges: [] };
      this.playground = playgroundRes.ok ? await playgroundRes.json() : { templates: [] };
      this.mascot = mascotRes.ok ? await mascotRes.json() : {};

      // Apply theme from config
      this.applyTheme();

      this.isLoaded = true;
      console.log(`ConfigService: Loaded ${ACTIVE_LANGUAGE} (${VERSION_TYPE})`);
      
      return this;
    } catch (error) {
      console.error('ConfigService: Failed to load configs', error);
      throw error;
    }
  }

  /**
   * Apply theme colors to CSS custom properties
   */
  applyTheme() {
    if (!this.config?.branding?.colors) return;

    const colors = this.config.branding.colors;
    const root = document.documentElement;

    // Set all color variables
    Object.keys(colors).forEach(key => {
      const cssVarName = `--color-${this.camelToKebab(key)}`;
      root.style.setProperty(cssVarName, colors[key]);
    });

    // Set additional UI variables from config
    if (this.config.ui) {
      const ui = this.config.ui;
      if (ui.fontFamily) root.style.setProperty('--font-family', ui.fontFamily);
      if (ui.fontFamilyCode) root.style.setProperty('--font-family-code', ui.fontFamilyCode);
      if (ui.fontSize) root.style.setProperty('--font-size-base', `${ui.fontSize}px`);
      if (ui.borderRadius) root.style.setProperty('--border-radius', `${ui.borderRadius}px`);
      if (ui.spacing) root.style.setProperty('--spacing-base', `${ui.spacing}px`);
    }
  }

  /**
   * Convert camelCase to kebab-case
   */
  camelToKebab(str) {
    return str.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
  }

  /**
   * Get the main app configuration
   */
  getConfig() {
    return this.config;
  }

  /**
   * Get app info
   */
  getAppInfo() {
    return this.config?.app || {};
  }

  /**
   * Get branding info
   */
  getBranding() {
    return this.config?.branding || {};
  }

  /**
   * Get mascot configuration
   */
  getMascotConfig() {
    return this.config?.mascot || {};
  }

  /**
   * Get execution configuration
   */
  getExecutionConfig() {
    return this.config?.execution || {};
  }

  /**
   * Get feature flags
   */
  getFeatures() {
    return this.config?.features || {};
  }

  /**
   * Check if a feature is enabled
   */
  isFeatureEnabled(featureName) {
    return this.config?.features?.[featureName] === true;
  }

  /**
   * Get gamification settings
   */
  getGamification() {
    return this.config?.gamification || {};
  }

  /**
   * Get monetization settings
   */
  getMonetization() {
    return this.config?.monetization || {};
  }

  /**
   * Check if ads are enabled
   */
  isAdsEnabled() {
    return this.config?.monetization?.enabled === true && 
           this.config.monetization.type === 'ads';
  }

  /**
   * Get ad configuration
   */
  getAdConfig() {
    if (!this.isAdsEnabled()) return null;
    return this.config.monetization.adConfig || {};
  }

  /**
   * Get ad unit IDs
   */
  getAdUnits() {
    if (!this.isAdsEnabled()) return null;
    return this.config.monetization.adUnits || {};
  }

  /**
   * Get UI configuration
   */
  getUIConfig() {
    return this.config?.ui || {};
  }

  /**
   * Get navigation tabs
   */
  getNavigationTabs() {
    return this.config?.navigation?.tabs || [];
  }

  /**
   * Get onboarding configuration
   */
  getOnboarding() {
    return this.config?.onboarding || {};
  }

  /**
   * Get all lessons
   */
  getLessons() {
    return this.lessons;
  }

  /**
   * Get lesson categories
   */
  getCategories() {
    return this.lessons?.categories || [];
  }

  /**
   * Get all lesson items
   */
  getLessonsList() {
    return this.lessons?.lessons || [];
  }

  /**
   * Get a specific lesson by ID
   */
  getLessonById(id) {
    return this.lessons?.lessons?.find(l => l.id === id) || null;
  }

  /**
   * Get lessons by category
   */
  getLessonsByCategory(categoryId) {
    return this.lessons?.lessons?.filter(l => l.categoryId === categoryId) || [];
  }

  /**
   * Get next lesson after the given lesson ID
   */
  getNextLesson(currentId) {
    const lessons = this.getLessonsList();
    const currentIndex = lessons.findIndex(l => l.id === currentId);
    if (currentIndex >= 0 && currentIndex < lessons.length - 1) {
      return lessons[currentIndex + 1];
    }
    return null;
  }

  /**
   * Get previous lesson before the given lesson ID
   */
  getPreviousLesson(currentId) {
    const lessons = this.getLessonsList();
    const currentIndex = lessons.findIndex(l => l.id === currentId);
    if (currentIndex > 0) {
      return lessons[currentIndex - 1];
    }
    return null;
  }

  /**
   * Get all interview questions
   */
  getInterviews() {
    return this.interviews;
  }

  /**
   * Get interview questions list
   */
  getInterviewQuestions() {
    return this.interviews?.questions || [];
  }

  /**
   * Get interview questions by category
   */
  getInterviewsByCategory(category) {
    return this.interviews?.questions?.filter(q => q.category === category) || [];
  }

  /**
   * Get all challenges
   */
  getChallenges() {
    return this.challenges;
  }

  /**
   * Get challenges list
   */
  getChallengesList() {
    return this.challenges?.challenges || [];
  }

  /**
   * Get a specific challenge by ID
   */
  getChallengeById(id) {
    return this.challenges?.challenges?.find(c => c.id === id) || null;
  }

  /**
   * Get all achievements/badges
   */
  getAchievements() {
    return this.achievements;
  }

  /**
   * Get badges list
   */
  getBadges() {
    return this.achievements?.badges || [];
  }

  /**
   * Get a specific badge by ID
   */
  getBadgeById(id) {
    return this.achievements?.badges?.find(b => b.id === id) || null;
  }

  /**
   * Get playground configuration
   */
  getPlayground() {
    return this.playground;
  }

  /**
   * Get code templates for playground
   */
  getCodeTemplates() {
    return this.playground?.templates || [];
  }

  /**
   * Get mascot data
   */
  getMascot() {
    return this.mascot;
  }

  /**
   * Get mascot messages by type
   */
  getMascotMessages(type) {
    return this.mascot?.messages?.[type] || [];
  }

  /**
   * Get a random mascot message for a situation
   */
  getRandomMascotMessage(type) {
    const messages = this.getMascotMessages(type);
    if (messages.length === 0) return '';
    return messages[Math.floor(Math.random() * messages.length)];
  }

  /**
   * Get the path for a media asset
   */
  getMediaPath(relativePath) {
    return `/assets/media/${relativePath}`;
  }

  /**
   * Get mascot image path by state
   */
  getMascotImage(state = 'idle') {
    const mascotConfig = this.getMascotConfig();
    const statePath = mascotConfig?.states?.[state] || mascotConfig?.states?.idle || 'mascot.svg';
    return this.getMediaPath(`${mascotConfig.basePath || 'mascots/python'}/${statePath}`);
  }

  /**
   * Get level info for a given points value
   */
  getLevelForPoints(points) {
    const levels = this.config?.gamification?.levels || [];
    let currentLevel = levels[0] || { level: 1, name: 'Beginner', points: 0 };
    
    for (const level of levels) {
      if (points >= level.points) {
        currentLevel = level;
      } else {
        break;
      }
    }
    
    return currentLevel;
  }

  /**
   * Get points needed for next level
   */
  getPointsForNextLevel(currentPoints) {
    const levels = this.config?.gamification?.levels || [];
    
    for (const level of levels) {
      if (level.points > currentPoints) {
        return level.points - currentPoints;
      }
    }
    
    return 0; // Max level reached
  }
}

// Export singleton instance
export const configService = new ConfigService();
export default configService;